import { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  CHART_AXIS,
  CHART_COLORS,
  CHART_TOOLTIP_ITEM_STYLE,
  CHART_TOOLTIP_STYLE,
} from './chart-theme';

export interface SeriesSpec<T> {
  key: keyof T & string;
  label: string;
  color?: string;
}

interface TimeSeriesChartProps<T extends { date: string }> {
  data: T[];
  series: SeriesSpec<T>[];
  height?: number;
  xFormatter?: (raw: string) => string;
}

export function TimeSeriesChart<T extends { date: string }>({
  data,
  series,
  height = 240,
  xFormatter,
}: TimeSeriesChartProps<T>): JSX.Element {
  const fmt =
    xFormatter ??
    ((raw: string) => {
      // raw is 'YYYY-MM-DD'; show 'MMM DD' to keep ticks short
      const [, m, d] = raw.split('-');
      if (!m || !d) return raw;
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const monthIndex = Math.max(0, Math.min(11, Number(m) - 1));
      return `${months[monthIndex]} ${Number(d)}`;
    });

  const resolved = useMemo(
    () =>
      series.map((s, i) => ({
        ...s,
        color: s.color ?? CHART_COLORS[i % CHART_COLORS.length],
      })),
    [series],
  );

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
        <CartesianGrid stroke={CHART_AXIS.stroke} vertical={false} />
        <XAxis
          dataKey="date"
          stroke={CHART_AXIS.stroke}
          tick={CHART_AXIS.tick}
          tickLine={false}
          tickFormatter={fmt}
          minTickGap={28}
        />
        <YAxis
          stroke={CHART_AXIS.stroke}
          tick={CHART_AXIS.tick}
          tickLine={false}
          axisLine={false}
          width={48}
          allowDecimals={false}
        />
        <Tooltip
          contentStyle={CHART_TOOLTIP_STYLE}
          itemStyle={CHART_TOOLTIP_ITEM_STYLE}
          labelStyle={{ color: '#CCCCCC', fontWeight: 700 }}
          cursor={{ stroke: 'rgba(255,255,255,0.15)' }}
        />
        {resolved.map((s) => (
          <Line
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.label}
            stroke={s.color}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}
